import { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  apiErrorText,
  useGetRequestQuery,
  useTransitionRequestMutation,
  useAttachRequestResultMutation,
  useConvertRequestToStatutMutation,
  useGetRequestLinksQuery,
  useGetRequestLinkCandidatesQuery,
  useLinkRequestMutation,
  useUnlinkRequestMutation,
} from '@shared/api';
import {
  MTO_SUPPLY,
  MtoSupplySummary,
  actionsForStatus,
  functionLabel,
  isLegalRequest,
  takesTextResponse,
  mtoSupplyRunsHref,
  requestTypeLabel,
  statusLabel,
  statusTone,
  priorityLabel,
  priorityTone,
  slaBadge,
  fmtRequestDate,
  type FunctionRequest,
} from '@entities/requests';
import { useAgentPermission } from '@entities/user';
import { RespondForm, ResponseView } from '@features/request-handling';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';

const BackLink = styled(Link)`
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const HeadRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const RTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const Meta = styled.div`
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(2)};
  line-height: 1.6;
`;

const Section = styled.section`
  margin-top: ${({ theme }) => theme.spacing(7)};
`;

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: center;
`;

const Input = styled.input`
  font-family: inherit;
  font-size: 14px;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  min-width: 260px;
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const TItem = styled(Card)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
`;

const TReason = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12.5px;
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const ErrText = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const LinkList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const LinkRow = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const LinkInfo = styled(Link)`
  text-decoration: none;
  color: inherit;
  flex: 1;
  min-width: 0;
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const LinkTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const LinkMeta = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const SchemeResultLink = styled(Link)`
  font-size: 14px;
  font-weight: 600;
`;

const RunsLink = styled(Link)`
  display: inline-block;
  margin-top: ${({ theme }) => theme.spacing(4)};
  font-size: 14px;
  font-weight: 600;
`;

/**
 * Что с ответом снабжения, пока его нет (по статусу заявки). Снабженцу —
 * что сделать дальше, автору заявки — где она сейчас.
 */
function supplyWaitingText(status: string, executor: boolean): string {
  switch (status) {
    case 'draft':
      return 'Заявка ещё не подана — снабжение её не видит. Нажмите «Подать» в разделе «Действия» ниже.';
    case 'submitted':
      return executor
        ? 'Примите заявку или верните её с причиной — кнопки в разделе «Действия» ниже.'
        : 'Заявка ждёт приёма в снабжении.';
    case 'accepted':
      return executor
        ? 'Возьмите заявку в работу (кнопка «В работу» в разделе «Действия» ниже) — после этого здесь появится форма ответа.'
        : 'Снабжение приняло заявку и возьмёт её в работу.';
    case 'in_progress':
      return 'Снабжение готовит ответ.';
    case 'rejected':
      return 'Снабжение вернуло заявку — причина в истории ниже. Заявку можно подать повторно.';
    default:
      return 'Ответа нет.';
  }
}

/**
 * Строка связанной/кандидатной заявки: клик — на её карточку; справа —
 * действие (отвязать / связать). Статус и тип берём из общих хелперов.
 */
function LinkItem({
  r,
  actionLabel,
  onAction,
  busy,
  danger,
}: {
  r: FunctionRequest;
  actionLabel: string;
  onAction: () => void;
  busy: boolean;
  danger?: boolean;
}) {
  return (
    <LinkRow>
      <LinkInfo to={`/requests/${r.id}`}>
        <LinkTitle>{r.title}</LinkTitle>
        <LinkMeta>
          {requestTypeLabel(r.request_type)} · {r.source_function} → {r.target_function}
        </LinkMeta>
      </LinkInfo>
      <Badge $tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge>
      <Button $variant={danger ? 'ghost' : 'secondary'} onClick={onAction} disabled={busy}>
        {actionLabel}
      </Button>
    </LinkRow>
  );
}

/**
 * Карточка заявки между функциями (`/requests/:id`): конверт + результат +
 * действия автомата (submit/accept/reject/progress/complete/close/cancel) +
 * история переходов. Допустимость действий и RBAC — на backend; здесь
 * показываем действия текущего статуса (зеркало `actionsForStatus`).
 */
export function RequestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const rid = id ?? '';
  const { data, isLoading, isError } = useGetRequestQuery(rid, { skip: !id });
  const [transition, { isLoading: transitioning }] = useTransitionRequestMutation();
  const [attachResult, { isLoading: attaching }] = useAttachRequestResultMutation();
  const [convertToStatut, { isLoading: converting }] = useConvertRequestToStatutMutation();
  const { data: linksData } = useGetRequestLinksQuery(rid, { skip: !id });
  const { data: candData } = useGetRequestLinkCandidatesQuery(rid, { skip: !id });
  const [linkRequest, { isLoading: linking }] = useLinkRequestMutation();
  const [unlinkRequest, { isLoading: unlinking }] = useUnlinkRequestMutation();

  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [resultRef, setResultRef] = useState('');
  // Ошибка — у той секции, где нажали кнопку: «Действия» видны не во всех
  // статусах, а связать заявку или прикрепить результат можно и без них.
  const [err, setErr] = useState<{ at: 'action' | 'result' | 'links'; text: string } | null>(
    null,
  );

  const req = data?.request;
  // Результат заявки на геодезию — id исполнительной схемы: ведём туда, где её
  // можно открыть (у геодезиста — мастер, у инженера ПТО — карточка проверки).
  const geodesyAccess = useAgentPermission('geodesy');
  const ptoAccess = useAgentPermission('pto');
  const mtoAccess = useAgentPermission('mto');
  const statutAccess = useAgentPermission('statut');
  const schemeHref = geodesyAccess.can_read
    ? '/agents/geodesy/schemes'
    : ptoAccess.can_read
      ? '/agents/pto/exec-schemes'
      : null;
  const transitions = data?.transitions ?? [];
  const response = data?.response ?? null;
  const links = linksData?.links ?? [];
  const candidates = candData?.candidates ?? [];

  const run = async (action: string, reason?: string) => {
    if (!req) return;
    setErr(null);
    try {
      await transition({ id: req.id, action, reason }).unwrap();
      setShowReject(false);
      setRejectReason('');
    } catch (e) {
      // `data.error` — объект {code, message}: в state кладём только текст, иначе
      // React падает на рендере объекта и страница становится белой.
      setErr({
        at: 'action',
        text: apiErrorText(e, 'Действие отклонено (проверьте права/условия перехода).'),
      });
    }
  };

  const attach = async () => {
    if (!req || !resultRef.trim()) return;
    setErr(null);
    try {
      await attachResult({ id: req.id, result_ref: resultRef.trim() }).unwrap();
      setResultRef('');
    } catch (e) {
      setErr({
        at: 'result',
        text: apiErrorText(
          e,
          'Не удалось прикрепить результат (нужен доступ к функции-обработчику).',
        ),
      });
    }
  };

  const doConvertToStatut = async () => {
    if (!req) return;
    setErr(null);
    try {
      const res = await convertToStatut(req.id).unwrap();
      if (res.entity_url && statutAccess.can_read) {
        navigate(res.entity_url);
      }
    } catch (e) {
      setErr({
        at: 'result',
        text: apiErrorText(e, 'Не удалось создать карточку в ОСНОВА.Юрист.'),
      });
    }
  };

  const doLink = async (otherId: string) => {
    setErr(null);
    try {
      await linkRequest({ id: rid, other_id: otherId }).unwrap();
    } catch (e) {
      setErr({
        at: 'links',
        text: apiErrorText(e, 'Не удалось связать заявку (проверьте права).'),
      });
    }
  };

  const doUnlink = async (otherId: string) => {
    setErr(null);
    try {
      await unlinkRequest({ id: rid, other_id: otherId }).unwrap();
    } catch (e) {
      setErr({ at: 'links', text: apiErrorText(e, 'Не удалось снять связь.') });
    }
  };

  if (isLoading) return <Muted>Загрузка заявки…</Muted>;
  if (isError || !req) return <Muted>Заявка не найдена или нет доступа.</Muted>;

  const isSupply = req.request_type === MTO_SUPPLY;
  const isLegal = isLegalRequest(req.request_type);
  const isTextRespondable = takesTextResponse(req.request_type);
  const legalPayload =
    isLegal && typeof req.payload === 'object' && req.payload !== null
      ? (req.payload as { counterparty?: string; inn?: string; comment?: string; description?: string })
      : null;
  const genericPayload =
    !isSupply && !isLegal && typeof req.payload === 'object' && req.payload !== null
      ? (req.payload as {
          comment?: string;
          description?: string;
          reason?: string;
          deviation_rub?: number;
          limit_rub?: number;
          specialty?: string;
          headcount?: number;
          meeting_id?: string;
        })
      : null;

  const statutEntityHref = (() => {
    if (!req.result_ref || !req.result_kind) return null;
    switch (req.result_kind) {
      case 'statut_contract':
        return statutAccess.can_read
          ? `/agents/statut/contracts/${req.result_ref}`
          : `/contracts/${req.result_ref}/brief`;
      case 'statut_claim':
        return statutAccess.can_read ? `/agents/statut/claims/${req.result_ref}` : null;
      case 'statut_letter':
        return statutAccess.can_read ? `/agents/statut/correspondence` : null;
      case 'statut_counterparty':
        return statutAccess.can_read ? `/agents/statut/counterparties/${req.result_ref}` : null;
      default:
        return null;
    }
  })();

  // Заявкам с текстовым ответом ответ даётся формой RespondForm — она же и выполняет заявку;
  // «Завершить» нужен, только если ответ уже есть, а заявка ещё в работе.
  const actions = actionsForStatus(req.status).filter(
    (a) => !(isTextRespondable && a.action === 'complete' && !req.result_ref),
  );
  const sla = slaBadge(req);
  // Ведомость по документам заявки — пока заявка живая (не черновик, не
  // возвращена и не отменена).
  const runsHref =
    isSupply && mtoAccess.can_read && !['draft', 'rejected', 'cancelled'].includes(req.status)
      ? mtoSupplyRunsHref(req)
      : null;
  // Снабженец пришёл из своих входящих — туда и возвращаем.
  const back =
    isSupply && mtoAccess.can_read
      ? { to: '/agents/mto/requests', label: '← Ко входящим заявкам снабжения' }
      : { to: '/requests', label: '← К реестру заявок' };

  return (
    <div style={{ maxWidth: 920, margin: '0 auto' }}>
      <BackLink to={back.to}>{back.label}</BackLink>

      <HeadRow>
        <RTitle>{req.title}</RTitle>
        <Badge $tone={statusTone(req.status)}>{statusLabel(req.status)}</Badge>
        <Badge $tone={priorityTone(req.priority)}>{priorityLabel(req.priority)}</Badge>
        {sla && <Badge $tone={sla.tone}>{sla.label}</Badge>}
      </HeadRow>
      <Meta>
        {requestTypeLabel(req.request_type)} · {functionLabel(req.source_function)} →{' '}
        {functionLabel(req.target_function)}
        <br />
        Создана: {fmtRequestDate(req.created_at)}
        {req.due_date && (
          <>
            {' '}
            · {isSupply ? 'нужно к' : 'срок'}: {fmtRequestDate(req.due_date)}
          </>
        )}
        {req.needs_review && (
          <>
            {' '}
            · <Badge $tone="warning">нужна проверка</Badge>
          </>
        )}
      </Meta>

      {isSupply && (
        <Section>
          <SectionLabel>Что нужно</SectionLabel>
          <Card>
            <MtoSupplySummary request={req} />
            {runsHref && <RunsLink to={runsHref}>Сформировать ведомость материалов →</RunsLink>}
          </Card>
        </Section>
      )}

      {isLegal && legalPayload && (legalPayload.counterparty || legalPayload.inn || legalPayload.comment || legalPayload.description) && (
        <Section>
          <SectionLabel>Параметры обращения в юрслужбу</SectionLabel>
          <Card>
            {legalPayload.counterparty && (
              <div style={{ fontSize: 14, marginBottom: 6 }}>
                <strong>Контрагент / адресат:</strong> {legalPayload.counterparty}
                {legalPayload.inn ? ` (ИНН ${legalPayload.inn})` : ''}
              </div>
            )}
            {!legalPayload.counterparty && legalPayload.inn && (
              <div style={{ fontSize: 14, marginBottom: 6 }}>
                <strong>ИНН контрагента:</strong> {legalPayload.inn}
              </div>
            )}
            {(legalPayload.comment || legalPayload.description) && (
              <div style={{ fontSize: 13.5, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                {legalPayload.comment ?? legalPayload.description}
              </div>
            )}
          </Card>
        </Section>
      )}

      {genericPayload &&
        (genericPayload.comment ||
          genericPayload.description ||
          genericPayload.reason ||
          genericPayload.specialty ||
          typeof genericPayload.deviation_rub === 'number' ||
          genericPayload.meeting_id) && (
          <Section>
            <SectionLabel>Детали и обоснование заявки</SectionLabel>
            <Card>
              {typeof genericPayload.deviation_rub === 'number' && (
                <div style={{ fontSize: 14, marginBottom: 6 }}>
                  <strong>Превышение лимита:</strong>{' '}
                  {genericPayload.deviation_rub.toLocaleString('ru-RU')} ₽
                  {typeof genericPayload.limit_rub === 'number'
                    ? ` (лимит: ${genericPayload.limit_rub.toLocaleString('ru-RU')} ₽)`
                    : ''}
                </div>
              )}
              {genericPayload.specialty && (
                <div style={{ fontSize: 14, marginBottom: 6 }}>
                  <strong>Специальность / роль:</strong> {genericPayload.specialty}
                  {typeof genericPayload.headcount === 'number'
                    ? ` — ${genericPayload.headcount} чел.`
                    : ''}
                </div>
              )}
              {genericPayload.meeting_id && (
                <div style={{ fontSize: 13.5, marginBottom: 6 }}>
                  <Link to={`/agents/meetings/${genericPayload.meeting_id}`}>
                    Открыть протокол совещания →
                  </Link>
                </div>
              )}
              {(genericPayload.comment || genericPayload.description || genericPayload.reason) && (
                <div style={{ fontSize: 13.5, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                  {genericPayload.comment ?? genericPayload.description ?? genericPayload.reason}
                </div>
              )}
            </Card>
          </Section>
        )}

      <Section>
        <SectionLabel>
          {isSupply
            ? 'Ответ снабжения'
            : isLegal
              ? 'Ответ и работа юрслужбы'
              : isTextRespondable
                ? `Ответ исполнителя (${functionLabel(req.target_function)})`
                : 'Результат'}
        </SectionLabel>
        {isLegal && statutEntityHref && (
          <Card style={{ marginBottom: 12 }}>
            <SchemeResultLink to={statutEntityHref}>
              {req.result_kind === 'statut_contract'
                ? statutAccess.can_read
                  ? 'Открыть карточку договора в ОСНОВА.Юрист →'
                  : 'Открыть паспорт договора →'
                : req.result_kind === 'statut_claim'
                  ? 'Открыть претензию в ОСНОВА.Юрист →'
                  : req.result_kind === 'statut_letter'
                    ? 'Открыть письмо в ОСНОВА.Юрист →'
                    : 'Открыть карточку контрагента в ОСНОВА.Юрист →'}
            </SchemeResultLink>
          </Card>
        )}
        {isLegal &&
          statutAccess.can_write &&
          !req.result_kind?.startsWith('statut_') &&
          ['submitted', 'accepted', 'in_progress'].includes(req.status) && (
            <Actions style={{ marginBottom: 12 }}>
              <Button
                $variant="secondary"
                onClick={() => void doConvertToStatut()}
                disabled={converting}
              >
                {converting
                  ? 'Создание карточки…'
                  : 'Создать карточку в ОСНОВА.Юрист →'}
              </Button>
            </Actions>
          )}
        {response ? (
          <Card>
            <ResponseView requestId={req.id} response={response} />
          </Card>
        ) : isSupply ? (
          req.status === 'in_progress' && mtoAccess.can_write ? (
            <Card>
              <RespondForm requestId={req.id} />
            </Card>
          ) : (
            <Muted>{supplyWaitingText(req.status, mtoAccess.can_write)}</Muted>
          )
        ) : isLegal ? (
          req.status === 'in_progress' && statutAccess.can_write ? (
            <Card>
              <RespondForm requestId={req.id} />
            </Card>
          ) : !statutEntityHref ? (
            <Muted>
              {statutAccess.can_write
                ? 'Примите заявку и переведите её «В работу», чтобы написать ответ с приложением документа, или создайте карточку в ОСНОВА.Юрист кнопкой выше.'
                : 'Заявка передана в юридическую службу.'}
            </Muted>
          ) : null
        ) : isTextRespondable ? (
          req.status === 'in_progress' ? (
            <Card>
              <RespondForm requestId={req.id} />
            </Card>
          ) : (
            <Muted>
              Переведите заявку в статус «В работу» (раздел «Действия» ниже), чтобы дать решение /
              ответ и автоматически завершить заявку.
            </Muted>
          )
        ) : req.result_ref && req.request_type === 'geodesy' && schemeHref ? (
          <Card>
            <SchemeResultLink to={`${schemeHref}/${req.result_ref}`}>
              Исполнительная схема по заявке →
            </SchemeResultLink>
          </Card>
        ) : req.result_ref ? (
          <Card>
            <div style={{ fontSize: 14 }}>
              {req.result_kind ?? 'артефакт'}: <code>{req.result_ref}</code>
            </div>
          </Card>
        ) : req.status === 'in_progress' ? (
          <Actions>
            <Input
              value={resultRef}
              onChange={(e) => setResultRef(e.target.value)}
              placeholder="UUID результата-артефакта (напр. АОСР)"
            />
            <Button
              $variant="secondary"
              onClick={attach}
              disabled={attaching || !resultRef.trim()}
            >
              {attaching ? 'Прикрепление…' : 'Прикрепить результат'}
            </Button>
          </Actions>
        ) : (
          <Muted>Результат ещё не прикреплён.</Muted>
        )}
        {err?.at === 'result' && <ErrText>{err.text}</ErrText>}
      </Section>

      {actions.length > 0 && (
        <Section>
          <SectionLabel>Действия</SectionLabel>
          <Actions>
            {actions.map((a) => {
              if (a.action === 'reject') {
                return (
                  <Button
                    key={a.action}
                    $variant="danger"
                    onClick={() => setShowReject((v) => !v)}
                    disabled={transitioning}
                  >
                    {a.label}
                  </Button>
                );
              }
              const disabled =
                transitioning || (a.needsResult && !req.result_ref);
              return (
                <Button
                  key={a.action}
                  $variant={a.action === 'cancel' ? 'ghost' : 'primary'}
                  onClick={() => run(a.action)}
                  disabled={disabled}
                  title={
                    a.needsResult && !req.result_ref
                      ? 'Сначала прикрепите результат-артефакт'
                      : undefined
                  }
                >
                  {a.label}
                </Button>
              );
            })}
          </Actions>
          {showReject && (
            <Actions style={{ marginTop: 12 }}>
              <Input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Причина возврата (обязательна)"
              />
              <Button
                $variant="danger"
                onClick={() => run('reject', rejectReason.trim())}
                disabled={transitioning || !rejectReason.trim()}
              >
                Вернуть
              </Button>
            </Actions>
          )}
          {err?.at === 'action' && <ErrText>{err.text}</ErrText>}
        </Section>
      )}

      <Section>
        <SectionLabel>Связанные заявки</SectionLabel>
        {links.length === 0 ? (
          <Muted>Связей нет.</Muted>
        ) : (
          <LinkList>
            {links.map((l) => (
              <LinkItem
                key={l.id}
                r={l}
                actionLabel={unlinking ? '…' : 'Отвязать'}
                onAction={() => doUnlink(l.id)}
                busy={unlinking}
                danger
              />
            ))}
          </LinkList>
        )}
        {candidates.length > 0 && (
          <>
            <SectionLabel style={{ marginTop: 20 }}>По этому элементу также</SectionLabel>
            <LinkList>
              {candidates.map((c) => (
                <LinkItem
                  key={c.id}
                  r={c}
                  actionLabel={linking ? '…' : 'Связать'}
                  onAction={() => doLink(c.id)}
                  busy={linking}
                />
              ))}
            </LinkList>
          </>
        )}
        {err?.at === 'links' && <ErrText>{err.text}</ErrText>}
      </Section>

      <Section>
        <SectionLabel>История</SectionLabel>
        {transitions.length === 0 ? (
          <Muted>Переходов нет.</Muted>
        ) : (
          <Timeline>
            {transitions.map((t) => (
              <TItem key={t.id}>
                <div>
                  {t.from_status ? (
                    <>
                      {statusLabel(t.from_status)} → <b>{statusLabel(t.to_status)}</b>
                    </>
                  ) : (
                    <>
                      создана (<b>{statusLabel(t.to_status)}</b>)
                    </>
                  )}
                  {t.reason && <TReason>Причина: {t.reason}</TReason>}
                </div>
                <Muted>{fmtRequestDate(t.created_at)}</Muted>
              </TItem>
            ))}
          </Timeline>
        )}
      </Section>
    </div>
  );
}
